import { DataCollectionMetadata } from '@jarvis/shell-commons/dist/interface/v1/analytics/types'

import { buildDeviceSetupCheckEvent } from './helpers/build-device-setup-check-event'
import { buildSimpleUiEvent } from './helpers/build-simple-ui-event'
import { publishCdmEvent } from './helpers/publish-cdm-event'
import {
  EventDispatcherConfig,
  DisplayEventDetails,
  ControlEventDetails,
  ResourceFetchingStatusDetails,
  ResourceResultDetails,
} from './types'

import { ACTIONS_ANALYTICS, ActionsAnalytics } from '@/constants/analytics'
import { enumerateMethods } from '@/services/analytics/helpers/enumerate-methods'

export class AnalyticsEventDispatcher {
  constructor(readonly dispatcherConfig: EventDispatcherConfig) {
    /**
     * It is necessary to enumerate all methods as all object key-values manipulators
     * (such as `Object.keys`, `Object.assign` among other function) will not be able to
     * identify the methods to work with, resulting in the further usage ({@link import('./create-analytics-manifest').createAnalyticsManifest})
     * losing the methods when lazy assigning the methods into the analytics manifest ref
     */
    enumerateMethods(this)
  }

  private fireSimpleUIEvent(
    action: ActionsAnalytics[keyof ActionsAnalytics],
    eventDetails?: DisplayEventDetails,
    metadata?: DataCollectionMetadata,
  ) {
    publishCdmEvent(
      buildSimpleUiEvent(this.dispatcherConfig, {
        ...eventDetails,
        action,
      }),
      metadata,
    )
  }

  fireDeviceSetupCheckEvent(
    resourceFetchingStatusDetails: ResourceFetchingStatusDetails,
    resourceResultDetails: ResourceResultDetails,
  ) {
    publishCdmEvent(
      buildDeviceSetupCheckEvent(
        resourceFetchingStatusDetails,
        resourceResultDetails,
      ),
    )
  }

  fireScreenDisplayed(
    eventDetails?: DisplayEventDetails,
    metadata?: DataCollectionMetadata,
  ) {
    this.fireSimpleUIEvent(
      ACTIONS_ANALYTICS.SCREEN_DISPLAYED,
      eventDetails,
      metadata,
    )
  }
  fireButtonClicked(eventDetails: ControlEventDetails) {
    this.fireSimpleUIEvent(
      ACTIONS_ANALYTICS.CONTROL_BUTTON_CLICKED,
      eventDetails,
    )
  }
  fireModalWindowDisplayed(eventDetails?: DisplayEventDetails) {
    this.fireSimpleUIEvent(
      ACTIONS_ANALYTICS.MODAL_WINDOW_DISPLAYED,
      eventDetails,
    )
  }
  fireControlDisplayed(eventDetails: ControlEventDetails) {
    this.fireSimpleUIEvent(ACTIONS_ANALYTICS.CONTROL_DISPLAYED, eventDetails)
  }
  fireHyperLinkClicked(eventDetails: ControlEventDetails) {
    this.fireSimpleUIEvent(
      ACTIONS_ANALYTICS.CONTROL_HYPERLINK_CLICKED,
      eventDetails,
    )
  }
  fireToggleEnabled(eventDetails: ControlEventDetails) {
    this.fireSimpleUIEvent(
      ACTIONS_ANALYTICS.CONTROL_TOGGLE_ENABLED,
      eventDetails,
    )
  }
  fireToggleDisabled(eventDetails: ControlEventDetails) {
    this.fireSimpleUIEvent(
      ACTIONS_ANALYTICS.CONTROL_TOGGLE_DISABLED,
      eventDetails,
    )
  }
  fireAccordionExpanded(eventDetails: ControlEventDetails) {
    this.fireSimpleUIEvent(
      ACTIONS_ANALYTICS.CONTROL_ACCORDIAN_EXPANDED,
      eventDetails,
    )
  }
  fireAccordionCollapsed(eventDetails: ControlEventDetails) {
    this.fireSimpleUIEvent(
      ACTIONS_ANALYTICS.CONTROL_ACCORDIAN_COLLAPSED,
      eventDetails,
    )
  }
}
